<script lang="ts" setup>
import {
  onBeforeUnmount, onMounted, computed, ref, watch,
} from 'vue';
import { CdrText, CdrList, CdrLink } from '@rei/cedar';
import type { InPageNavProps } from '@returns-page/types/index.mts';
import useIntersector from '@composables/use-intersector.mts';

const props = withDefaults(defineProps<InPageNavProps>(), {
  navModel: () => [],
  faqTitle: 'Frequently Asked Questions',
});

const elementsToObserve = ref<HTMLElement[]>([]);
const listRef = ref();
const activeIndex = ref(0);

const visibleSections = useIntersector(elementsToObserve, {
  threshold: 0.5,
});

watch(
  visibleSections,
  (newVal) => {
    const index = newVal.indexOf(1);
    activeIndex.value = index === -1 ? 0 : index;
  },
  { deep: true },
);

const navModelWithFaq = computed(() => [
  ...(props.navModel || []),
  {
    title: props.faqTitle,
    id: 'frequently-asked-questions',
  },
]);

/* c8 ignore start */
const checkURL = () => {
  if (typeof window !== 'undefined') {
    // ensure the id of the accordion you want to open is the same as
    // the id of the hash in the browser
    const windowUrlHash = window.location.hash.substring(1);
    const accordionId = document.getElementById(windowUrlHash)?.id;

    if (windowUrlHash === accordionId) {
      document.getElementById(accordionId)?.click();
    }
  }
};
/* c8 ignore end */

/* c8 ignore start */
onMounted(() => {
  const filteredElements = navModelWithFaq.value
    .map(({ id }) => document.getElementById(id))
    .filter((el) => el !== null) as HTMLElement[];

  elementsToObserve.value = filteredElements;

  window?.addEventListener('hashchange', checkURL);
});

onBeforeUnmount(() => {
  window?.removeEventListener('hashchange', checkURL);
});
/* c8 ignore end */
</script>

<template>
  <nav
    id="in-page-nav"
    class="in-page-nav"
    data-ui="in-page-nav-component"
    aria-label="Quick links"
  >
    <cdr-text class="in-page-nav__label">
      Quick links
    </cdr-text>
    <cdr-list
      ref="listRef"
      class="in-page-nav__list"
    >
      <li
        v-for="(nav, index) in navModelWithFaq"
        :key="`${nav.id}-${index}`"
        :class="['in-page-nav__item', { 'in-page-nav__item--active': index === activeIndex }]"
      >
        <cdr-link
          :id="`nav-${nav.id}`"
          :href="`#${nav.id}`"
          data-ui="in-page-nav-link"
          modifier="standalone"
        >
          {{ nav.title }}
        </cdr-link>
      </li>
    </cdr-list>
  </nav>
</template>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.in-page-nav {
  padding-left: $cdr-space-one-x;
  padding-top: $cdr-space-one-x;

  @include cdr-sm-mq-up {
    position: sticky;
    top: 0;
    height: 100vh;
    padding-left: $cdr-space-two-x;
    padding-top: $cdr-space-two-x;
  }

  &__label {
    @include cdr-text-heading-sans-500;

    display: none;

    @include cdr-xs-mq-only {
      display: block;
      margin-bottom: $cdr-space-one-and-a-half-x;
    }
  }

  & &__item{
    position: relative;

    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      display: none;
      height: 100%;

      @include cdr-sm-mq-up {
        border-right: 4px solid $cdr-color-text-link-rest;
      }
    }

    &--active {
      &::after {
        display: initial;
      }
    }
  }

  & &__list {
    position: relative;

    @include cdr-xs-mq-only {
      padding-left: $cdr-space-one-and-a-half-x;
    }

    & li {
      & a {
        width: 100%;
        text-decoration: underline;

        @include cdr-sm-mq-up {
          padding: $cdr-space-one-x $cdr-space-one-x $cdr-space-one-x 0;
          text-decoration: none;
          color: $cdr-color-text-primary;
        }
      }

      & a:active,
      a:focus {
        @include cdr-sm-mq-up {
          outline: none;
        }
      }
    }

    & li::before {
      @include cdr-xs-mq-only {
        content: '\2022';
        position: relative;
        margin-left: -1em;
        padding-right: $cdr-space-half-x;
        color: rgba(66, 59, 47, 0.75);
      }
    }
  }
}
</style>
